/**
 * PageSelector — Scrollable thumbnail strip for selecting PDF pages
 * to include in a banner ad as a cover image or page spreads.
 *
 * Creates all DOM elements programmatically inside the provided container.
 * Styled with a dark theme and brand accent colours.
 *
 * Selection model:
 *   - First selected page → Cover
 *   - Subsequent selections are paired into spreads (left / right)
 *   - Visual badges ("COVER", "SPREAD 1 L", "SPREAD 1 R") update live
 *
 * @module PageSelector
 */

/**
 * @typedef {Object} PageSelectorOptions
 * @property {number}   [thumbnailHeight=120]   Height of each thumbnail (px)
 * @property {(cover: HTMLCanvasElement) => void}  [onCoverSelect]
 * @property {(spreads: SpreadPair[]) => void}     [onPageSelect]
 */

/**
 * @typedef {Object} SpreadPair
 * @property {HTMLCanvasElement} leftPage
 * @property {HTMLCanvasElement} rightPage
 */

export class PageSelector {
  /** @type {HTMLElement} */
  #container;

  /** @type {number} */
  #thumbHeight;

  /** @type {number} Scale factor for high-res page extraction (affects render quality) */
  #renderScale;

  /** @type {((cover: HTMLCanvasElement) => void) | null} */
  #onCoverSelect;

  /** @type {((spreads: SpreadPair[]) => void) | null} */
  #onPageSelect;

  /** @type {HTMLDivElement} */
  #root;

  /** @type {HTMLDivElement} */
  #strip;

  /** @type {HTMLDivElement} */
  #statusBar;

  /**
   * All thumbnail items in DOM order.
   * Each entry holds references needed for selection logic.
   * @type {Array<{
   *   element: HTMLDivElement,
   *   canvas: HTMLCanvasElement,
   *   pageNumber: number,
   *   selected: boolean,
   *   badge: HTMLDivElement
   * }>}
   */
  #items = [];

  /**
   * Ordered list of selected page indices (into #items).
   * @type {number[]}
   */
  #selectionOrder = [];

  /**
   * @param {HTMLElement} containerElement
   * @param {PageSelectorOptions} [options]
   */
  constructor(containerElement, options = {}) {
    this.#container = containerElement;
    this.#thumbHeight = options.thumbnailHeight ?? 120;
    this.#renderScale = options.renderScale ?? 3.0;
    this.#onCoverSelect = options.onCoverSelect ?? null;
    this.#onPageSelect = options.onPageSelect ?? null;

    this.#injectStyles();
    this.#buildShell();
  }

  // ── Public API ──────────────────────────────────────────────────────

  /**
   * Populate the strip with thumbnails from a PdfExtractor.
   *
   * @param {import('./PdfExtractor.js').PdfExtractor} pdfExtractor
   *   Must already have a PDF loaded via `loadPdf()`.
   */
  async loadFromExtractor(pdfExtractor) {
    this.clear();

    const total = pdfExtractor.getPageCount();
    if (total === 0) return;

    this.#setStatus(`Rendering ${total} page${total > 1 ? 's' : ''}…`);

    // Extract at high resolution for quality rendering; thumbnails are drawn scaled-down
    for (let i = 1; i <= total; i++) {
      try {
        const page = await pdfExtractor.extractPage(i, this.#renderScale);
        await this.#addThumbnail(page);
        this.#setStatus(`Rendered page ${i} of ${total}`);
      } catch (err) {
        console.warn(`PageSelector: skipping page ${i}:`, err);
        this.#addErrorThumbnail(i);
      }
    }

    this.#setStatus(`${total} page${total > 1 ? 's' : ''} loaded — click to select cover & spreads`);

    // Auto-select: page 1 as cover, pages 4 & 5 as first spread
    this.#autoSelectDefaults(total);
  }

  /**
   * Auto-select a sensible default set of pages:
   *   page 1 → cover
   *   pages 4 & 5 → first spread (left / right)
   * @param {number} total - total number of pages available
   * @private
   */
  #autoSelectDefaults(total) {
    if (total === 0) return;
    this.#clearSelection();

    // Page 1 (index 0) as cover
    this.#selectIndex(0);

    // Pages 4 & 5 (indices 3 & 4) as first spread
    if (total >= 5) {
      this.#selectIndex(3);
      this.#selectIndex(4);
    } else if (total >= 4) {
      this.#selectIndex(3);
    } else if (total >= 3) {
      // Not enough for a full spread from page 4, use pages 2 & 3
      this.#selectIndex(1);
      this.#selectIndex(2);
    }

    this.#refreshBadges();
    this.#notifyCallbacks();
  }

  /**
   * Select a thumbnail by index without calling callbacks.
   * @param {number} idx
   * @private
   */
  #selectIndex(idx) {
    if (idx < 0 || idx >= this.#items.length) return;
    if (this.#items[idx].selected) return;
    this.#items[idx].selected = true;
    this.#items[idx].element.classList.add('bac-ps__thumb--selected');
    this.#selectionOrder.push(idx);
  }

  /**
   * Returns the canvas for the first selected page (the cover).
   * @returns {HTMLCanvasElement | null}
   */
  getSelectedCover() {
    if (this.#selectionOrder.length === 0) return null;
    return this.#items[this.#selectionOrder[0]].canvas;
  }

  /**
   * Returns spread pairs from the selection (all selected pages after
   * the cover, paired into consecutive left/right pairs).
   *
   * @returns {SpreadPair[]}
   */
  getSelectedSpreads() {
    if (this.#selectionOrder.length < 2) return [];

    // Everything after the first selection forms spreads
    const spreadIndices = this.#selectionOrder.slice(1);
    const spreads = [];

    for (let i = 0; i < spreadIndices.length; i += 2) {
      if (i + 1 < spreadIndices.length) {
        spreads.push({
          leftPage: this.#items[spreadIndices[i]].canvas,
          rightPage: this.#items[spreadIndices[i + 1]].canvas,
        });
      }
      // Odd trailing page is ignored — need pairs
    }

    return spreads;
  }

  /**
   * Remove all thumbnails and reset selection state.
   */
  clear() {
    this.#items = [];
    this.#selectionOrder = [];
    if (this.#strip) this.#strip.innerHTML = '';
    this.#setStatus('No PDF loaded');
  }

  // ── Private: Shell / DOM ────────────────────────────────────────────

  /** @private */
  #buildShell() {
    this.#root = document.createElement('div');
    this.#root.className = 'bac-ps';

    // ── Header row ────────────────────────────────────────────────────
    const header = document.createElement('div');
    header.className = 'bac-ps__header';

    const title = document.createElement('h3');
    title.className = 'bac-ps__title';
    title.textContent = 'Select Pages';

    const hint = document.createElement('span');
    hint.className = 'bac-ps__hint';
    hint.textContent = '1st click = cover · subsequent pairs = spreads';

    // Action buttons
    const actions = document.createElement('div');
    actions.className = 'bac-ps__actions';

    const autoBtn = document.createElement('button');
    autoBtn.className = 'bac-ps__btn';
    autoBtn.textContent = 'Auto-assign';
    autoBtn.title = 'Page 1 as cover, remaining pages paired as spreads';
    autoBtn.addEventListener('click', () => this.#autoAssign());

    const clearBtn = document.createElement('button');
    clearBtn.className = 'bac-ps__btn';
    clearBtn.textContent = 'Clear All';
    clearBtn.addEventListener('click', () => this.#clearSelection());

    actions.appendChild(autoBtn);
    actions.appendChild(clearBtn);

    header.appendChild(title);
    header.appendChild(hint);
    header.appendChild(actions);

    // ── Scrollable strip ──────────────────────────────────────────────
    const scrollOuter = document.createElement('div');
    scrollOuter.className = 'bac-ps__scroll';

    this.#strip = document.createElement('div');
    this.#strip.className = 'bac-ps__strip';
    scrollOuter.appendChild(this.#strip);

    // Translate vertical wheel events to horizontal scroll so users don't
    // need to hold Shift to scroll the thumbnail strip.
    scrollOuter.addEventListener('wheel', (e) => {
      if (e.deltaY !== 0 && scrollOuter.scrollWidth > scrollOuter.clientWidth) {
        e.preventDefault();
        scrollOuter.scrollLeft += e.deltaY;
      }
    }, { passive: false });

    // ── Status bar ────────────────────────────────────────────────────
    this.#statusBar = document.createElement('div');
    this.#statusBar.className = 'bac-ps__status';
    this.#statusBar.textContent = 'No PDF loaded';

    this.#root.appendChild(header);
    this.#root.appendChild(scrollOuter);
    this.#root.appendChild(this.#statusBar);
    this.#container.appendChild(this.#root);
  }

  // ── Private: Thumbnails ─────────────────────────────────────────────

  /**
   * Create a single thumbnail card from a rendered page.
   * @param {import('./PdfExtractor.js').PageResult} page
   * @private
   */
  async #addThumbnail(page) {
    const idx = this.#items.length;

    const wrapper = document.createElement('div');
    wrapper.className = 'bac-ps__thumb';
    wrapper.title = `Page ${page.pageNumber}`;

    // Scale canvas to thumbnail height while preserving aspect ratio
    const aspect = page.width / page.height;
    const thumbW = Math.round(this.#thumbHeight * aspect);

    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbW;
    thumbCanvas.height = this.#thumbHeight;
    const ctx = thumbCanvas.getContext('2d');
    
    // High-quality Lanczos resampling
    const bitmap = await createImageBitmap(page.canvas, {
      resizeWidth: thumbW,
      resizeHeight: this.#thumbHeight,
      resizeQuality: 'high'
    });
    ctx.drawImage(bitmap, 0, 0);
    
    thumbCanvas.className = 'bac-ps__thumb-img';
    wrapper.appendChild(thumbCanvas);

    // Page number label
    const label = document.createElement('div');
    label.className = 'bac-ps__thumb-label';
    label.textContent = `${page.pageNumber}`;
    wrapper.appendChild(label);

    // Badge (hidden until selected)
    const badge = document.createElement('div');
    badge.className = 'bac-ps__badge';
    wrapper.appendChild(badge);

    // Click handler
    wrapper.addEventListener('click', () => this.#toggleSelection(idx));

    // Right-click context menu
    wrapper.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.#showContextMenu(e, idx);
    });

    this.#strip.appendChild(wrapper);

    this.#items.push({
      element: wrapper,
      canvas: page.canvas,     // full-res canvas for later use
      pageNumber: page.pageNumber,
      selected: false,
      badge,
    });
  }

  /**
   * Placeholder for pages that failed to render.
   * @param {number} pageNumber
   * @private
   */
  #addErrorThumbnail(pageNumber) {
    const idx = this.#items.length;
    const wrapper = document.createElement('div');
    wrapper.className = 'bac-ps__thumb bac-ps__thumb--error';

    const errorLabel = document.createElement('div');
    errorLabel.className = 'bac-ps__thumb-error';
    errorLabel.textContent = `Page ${pageNumber}\nFailed`;
    wrapper.appendChild(errorLabel);

    const badge = document.createElement('div');
    badge.className = 'bac-ps__badge';
    wrapper.appendChild(badge);

    // Create a tiny blank canvas to keep the data model consistent
    const blankCanvas = document.createElement('canvas');
    blankCanvas.width = 1;
    blankCanvas.height = 1;

    wrapper.addEventListener('click', () => this.#toggleSelection(idx));

    wrapper.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.#showContextMenu(e, idx);
    });

    this.#strip.appendChild(wrapper);

    this.#items.push({
      element: wrapper,
      canvas: blankCanvas,
      pageNumber,
      selected: false,
      badge,
    });
  }

  // ── Private: Selection ──────────────────────────────────────────────

  /**
   * Toggle a thumbnail's selected state and recalculate badges.
   * @param {number} idx  Index into #items
   * @private
   */
  #toggleSelection(idx) {
    const item = this.#items[idx];
    if (!item) return;

    if (item.selected) {
      // Deselect
      item.selected = false;
      item.element.classList.remove('bac-ps__thumb--selected');
      this.#selectionOrder = this.#selectionOrder.filter((i) => i !== idx);
    } else {
      // Select
      item.selected = true;
      item.element.classList.add('bac-ps__thumb--selected');
      this.#selectionOrder.push(idx);
    }

    this.#refreshBadges();
    this.#notifyCallbacks();
  }

  /**
   * Update all badge labels to reflect the current selection order.
   * @private
   */
  #refreshBadges() {
    // Clear all badges first
    for (const item of this.#items) {
      item.badge.textContent = '';
      item.badge.className = 'bac-ps__badge';
    }

    if (this.#selectionOrder.length === 0) return;

    // First selection = cover
    const coverIdx = this.#selectionOrder[0];
    this.#items[coverIdx].badge.textContent = 'COVER';
    this.#items[coverIdx].badge.classList.add('bac-ps__badge--cover');

    // Remaining selections pair into spreads
    const rest = this.#selectionOrder.slice(1);
    let spreadNum = 1;

    for (let i = 0; i < rest.length; i += 2) {
      const leftItem = this.#items[rest[i]];
      leftItem.badge.textContent = `SPREAD ${spreadNum} L`;
      leftItem.badge.classList.add('bac-ps__badge--spread');

      if (i + 1 < rest.length) {
        const rightItem = this.#items[rest[i + 1]];
        rightItem.badge.textContent = `SPREAD ${spreadNum} R`;
        rightItem.badge.classList.add('bac-ps__badge--spread');
      }

      spreadNum++;
    }

    // Update status bar
    const cover = 1;
    const spreadCount = Math.floor(rest.length / 2);
    const unpaired = rest.length % 2;
    let statusParts = [`${cover} cover`];
    if (spreadCount > 0)
      statusParts.push(`${spreadCount} spread${spreadCount > 1 ? 's' : ''}`);
    if (unpaired > 0)
      statusParts.push(`1 unpaired page (needs a partner)`);

    this.#setStatus(`Selected: ${statusParts.join(', ')}`);
  }

  /**
   * Fire the onCoverSelect / onPageSelect callbacks.
   * @private
   */
  #notifyCallbacks() {
    if (this.#onCoverSelect) {
      this.#onCoverSelect(this.getSelectedCover());
    }
    if (this.#onPageSelect) {
      this.#onPageSelect(this.getSelectedSpreads());
    }
  }

  // ── Private: Auto-assign & Clear ────────────────────────────────────

  /**
   * Auto-assign: page 1 as cover, remaining pages paired as spreads.
   * @private
   */
  #autoAssign() {
    this.#clearSelection();
    if (this.#items.length === 0) return;
    // Select all items in order
    for (let i = 0; i < this.#items.length; i++) {
      this.#items[i].selected = true;
      this.#items[i].element.classList.add('bac-ps__thumb--selected');
      this.#selectionOrder.push(i);
    }
    this.#refreshBadges();
    this.#notifyCallbacks();
  }

  /**
   * Clear all selections without removing thumbnails.
   * @private
   */
  #clearSelection() {
    for (const item of this.#items) {
      item.selected = false;
      item.element.classList.remove('bac-ps__thumb--selected');
      item.badge.textContent = '';
      item.badge.className = 'bac-ps__badge';
    }
    this.#selectionOrder = [];
    this.#setStatus('No pages selected — click to select cover & spreads');
    this.#notifyCallbacks();
  }

  /**
   * Show a context menu for a thumbnail.
   * @param {MouseEvent} e
   * @param {number} idx
   * @private
   */
  #showContextMenu(e, idx) {
    // Remove any existing menu
    document.querySelector('.bac-ps__ctxmenu')?.remove();

    const menu = document.createElement('div');
    menu.className = 'bac-ps__ctxmenu';
    menu.style.left = `${e.clientX}px`;
    menu.style.top = `${e.clientY}px`;

    const makeItem = (label, action) => {
      const item = document.createElement('div');
      item.className = 'bac-ps__ctxmenu-item';
      item.textContent = label;
      item.addEventListener('click', () => {
        action();
        menu.remove();
      });
      return item;
    };

    menu.appendChild(makeItem('Set as Cover', () => this.#setAsCover(idx)));
    menu.appendChild(makeItem('Add as Spread Page', () => this.#addAsSpread(idx)));
    menu.appendChild(makeItem('Clear', () => this.#deselectItem(idx)));

    document.body.appendChild(menu);

    // Close on click elsewhere
    const closeHandler = () => {
      menu.remove();
      document.removeEventListener('click', closeHandler);
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  /**
   * Set a specific page as the cover (first in selection order).
   * @param {number} idx
   * @private
   */
  #setAsCover(idx) {
    // Remove from current position if selected
    this.#selectionOrder = this.#selectionOrder.filter(i => i !== idx);
    // Insert at front
    this.#selectionOrder.unshift(idx);
    if (!this.#items[idx].selected) {
      this.#items[idx].selected = true;
      this.#items[idx].element.classList.add('bac-ps__thumb--selected');
    }
    this.#refreshBadges();
    this.#notifyCallbacks();
  }

  /**
   * Add a page to the end of the selection (as a spread page).
   * @param {number} idx
   * @private
   */
  #addAsSpread(idx) {
    if (this.#items[idx].selected) return;
    this.#items[idx].selected = true;
    this.#items[idx].element.classList.add('bac-ps__thumb--selected');
    this.#selectionOrder.push(idx);
    this.#refreshBadges();
    this.#notifyCallbacks();
  }

  /**
   * Deselect a specific item.
   * @param {number} idx
   * @private
   */
  #deselectItem(idx) {
    if (!this.#items[idx].selected) return;
    this.#items[idx].selected = false;
    this.#items[idx].element.classList.remove('bac-ps__thumb--selected');
    this.#selectionOrder = this.#selectionOrder.filter(i => i !== idx);
    this.#refreshBadges();
    this.#notifyCallbacks();
  }

  // ── Private: Utilities ──────────────────────────────────────────────

  /**
   * @param {string} text
   * @private
   */
  #setStatus(text) {
    if (this.#statusBar) this.#statusBar.textContent = text;
  }

  /**
   * Inject scoped styles once per page load.
   * @private
   */
  #injectStyles() {
    if (document.getElementById('bac-ps-styles')) return;

    const style = document.createElement('style');
    style.id = 'bac-ps-styles';
    style.textContent = /* css */ `
      /* ─── Root container ────────────────────────────────────────── */
      .bac-ps {
        background: #1a1a1a;
        border: 1px solid #2a2a2a;
        border-radius: 10px;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
          Oxygen, Ubuntu, sans-serif;
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      /* ─── Header ────────────────────────────────────────────────── */
      .bac-ps__header {
        display: flex;
        align-items: baseline;
        gap: 12px;
        padding: 12px 16px 8px;
        border-bottom: 1px solid #2a2a2a;
      }

      .bac-ps__title {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: #e8e8e8;
        letter-spacing: 0.01em;
      }

      .bac-ps__hint {
        font-size: 12px;
        color: #666;
        font-style: italic;
      }

      .bac-ps__actions {
        margin-left: auto;
        display: flex;
        gap: 6px;
      }

      .bac-ps__btn {
        background: #2a2a2a;
        border: 1px solid #3a3a3a;
        color: #ccc;
        font-size: 12px;
        padding: 4px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-family: inherit;
      }

      .bac-ps__btn:hover {
        background: #3a3a3a;
        color: #fff;
      }

      .bac-ps__ctxmenu {
        position: fixed;
        z-index: 1000;
        background: #2a2a2a;
        border: 1px solid #3a3a3a;
        border-radius: 6px;
        padding: 4px 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        min-width: 160px;
      }

      .bac-ps__ctxmenu-item {
        padding: 8px 16px;
        font-size: 13px;
        color: #ddd;
        cursor: pointer;
        font-family: inherit;
      }

      .bac-ps__ctxmenu-item:hover {
        background: #3a3a3a;
        color: #fff;
      }

      /* ─── Scroll wrapper ────────────────────────────────────────── */
      .bac-ps__scroll {
        overflow-x: auto;
        overflow-y: hidden;
        padding: 14px 16px;
        scrollbar-width: thin;
        scrollbar-color: #444 #1a1a1a;
        flex: 1;
        min-height: 0;
      }

      .bac-ps__scroll::-webkit-scrollbar {
        height: 6px;
      }

      .bac-ps__scroll::-webkit-scrollbar-track {
        background: #1a1a1a;
      }

      .bac-ps__scroll::-webkit-scrollbar-thumb {
        background: #444;
        border-radius: 3px;
      }

      .bac-ps__scroll::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      /* ─── Thumbnail strip ───────────────────────────────────────── */
      .bac-ps__strip {
        display: flex;
        gap: 10px;
        align-items: stretch;
      }

      /* ─── Individual thumbnail ──────────────────────────────────── */
      .bac-ps__thumb {
        position: relative;
        flex: 0 0 auto;
        cursor: pointer;
        border-radius: 6px;
        border: 2px solid transparent;
        background: #222;
        overflow: hidden;
        transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
        user-select: none;
      }

      .bac-ps__thumb:hover {
        border-color: #444;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
      }

      .bac-ps__thumb--selected {
        border-color: #1d76ba !important;
        box-shadow: 0 0 0 1px #1d76ba, 0 4px 20px rgba(29, 118, 186, 0.35);
      }

      .bac-ps__thumb--selected:hover {
        border-color: #428bca !important;
      }

      .bac-ps__thumb--error {
        width: 80px;
        min-height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .bac-ps__thumb-error {
        color: #d84627;
        font-size: 11px;
        text-align: center;
        white-space: pre-line;
        padding: 8px;
      }

      /* ─── Thumbnail image ───────────────────────────────────────── */
      .bac-ps__thumb-img {
        display: block;
        height: 200px;
        width: auto;
      }

      /* ─── Page number label ─────────────────────────────────────── */
      .bac-ps__thumb-label {
        position: absolute;
        bottom: 4px;
        right: 4px;
        background: rgba(0, 0, 0, 0.7);
        color: #aaa;
        font-size: 11px;
        font-weight: 500;
        padding: 1px 6px;
        border-radius: 4px;
        line-height: 1.4;
        pointer-events: none;
        font-variant-numeric: tabular-nums;
      }

      .bac-ps__thumb--selected .bac-ps__thumb-label {
        color: #ccc;
      }

      /* ─── Selection badge ───────────────────────────────────────── */
      .bac-ps__badge {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        text-align: center;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        padding: 3px 6px;
        pointer-events: none;
        white-space: nowrap;
        line-height: 1.3;
        opacity: 0;
        transform: translateY(-4px);
        transition: opacity 0.2s, transform 0.2s;
      }

      .bac-ps__badge--cover,
      .bac-ps__badge--spread {
        opacity: 1;
        transform: translateY(0);
      }

      .bac-ps__badge--cover {
        background: linear-gradient(135deg, #d84627 0%, #d8583c 100%);
        color: #fff;
      }

      .bac-ps__badge--spread {
        background: linear-gradient(135deg, #1d76ba 0%, #428bca 100%);
        color: #fff;
      }

      /* ─── Status bar ────────────────────────────────────────────── */
      .bac-ps__status {
        padding: 8px 16px;
        font-size: 12px;
        color: #666;
        border-top: 1px solid #2a2a2a;
        background: #161616;
      }
    `;
    document.head.appendChild(style);
  }
}
